
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState } from "react";

interface CartFooterProps {
  calculateTotal: () => number;
  loading: boolean;
  cartItems: any[];
  digitalSignature: string;
  paymentMethod: string;
  onCancel: () => void;
  onValidate: () => void;
  isCredit?: boolean;
  partialPayment?: string;
}

export const CartFooter = ({
  calculateTotal,
  loading,
  cartItems,
  digitalSignature,
  paymentMethod,
  onCancel,
  onValidate,
  isCredit = false,
  partialPayment = "0",
}: CartFooterProps) => {
  const [amountReceived, setAmountReceived] = useState<string>("");
  const total = calculateTotal();
  
  const isDisabled = loading || 
    !cartItems?.length || 
    !digitalSignature?.trim() || 
    (!isCredit && !paymentMethod?.trim()) || 
    (isCredit && parseFloat(partialPayment) > 0 && !paymentMethod?.trim());

  const handleValidate = () => {
    if (!window.confirm("Attention : Avez-vous bien encaissé ce montant avant de le valider ?")) {
      return;
    }
    onValidate();
  };

  const handleAmountReceivedChange = (value: string) => {
    setAmountReceived(value);
  };

  const calculateDifference = () => {
    const received = parseFloat(amountReceived) || 0;
    const difference = received - total;

    if (received === 0) return null;

    if (difference === 0) {
      return {
        message: "Pas de monnaie à rendre",
        type: "exact",
        amount: 0
      };
    } else if (difference < 0) {
      return {
        message: "Montant insuffisant. Montant à ajouter:",
        type: "insufficient",
        amount: Math.abs(difference)
      };
    } else {
      return {
        message: "Monnaie à rendre:",
        type: "change",
        amount: difference
      };
    }
  };

  const difference = calculateDifference();

  return (
    <div className="flex flex-col space-y-4">
      <div className="flex justify-between items-center">
        <span className="text-lg font-semibold">Total:</span>
        <span className="text-lg font-bold">{total.toLocaleString()} FCFA</span>
      </div>
      
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <Input
            type="number"
            placeholder="Mettez ici le montant que le client vous donne pour savoir la monnaie à rendre"
            value={amountReceived}
            onChange={(e) => handleAmountReceivedChange(e.target.value)}
            min={0}
            className="text-right"
          />
        </div>
        {difference && (
          <div className="flex-1">
            <div className={`p-2 rounded-md text-right ${
              difference.type === 'insufficient' 
                ? 'bg-destructive/10 text-destructive'
                : difference.type === 'exact'
                ? 'bg-green-500/10 text-green-500'
                : 'bg-primary/10 text-primary'
            }`}>
              <span className="text-sm text-muted-foreground">{difference.message}</span>
              <span className="ml-2 font-bold">
                {difference.amount.toLocaleString()} FCFA
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-end space-x-4">
        <Button variant="outline" onClick={onCancel}>
          Annuler
        </Button>
        <Button 
          onClick={handleValidate} 
          disabled={isDisabled}
        >
          {loading ? "Validation..." : "Valider le panier"}
        </Button>
      </div>
    </div>
  );
};
